import { t } from "@lingui/macro";
import { captureEvent } from "@sentry/react";
import {
  type CreateExpenseBudgetItemMutation,
  type CreateOurWorkBudgetItemMutation,
  OurWorkOverviewDoneTasksForProjectDocument,
  type OurWorkOverviewDoneTasksForProjectQuery,
  type OurWorkOverviewDoneTasksForProjectQueryVariables,
  OurWorkOverviewForProjectDocument,
  type OurWorkOverviewForProjectQuery,
  type OurWorkOverviewForProjectQueryVariables,
  ProjectBillingPlanDataDocument,
  type ProjectBillingPlanDataQuery,
  type ProjectBillingPlanDataQueryVariables,
  type ProjectDetailQuery,
  type ProjectGraphQuery,
  type ProjectGraphQueryVariables,
  ProjectManagersSelectOptionsDocument,
  type ProjectManagersSelectOptionsQuery,
  type ProjectManagersSelectOptionsQueryVariables,
  type ProjectStageFragment,
  type SimpleTaskForClientFragment,
  type SimpleTaskFragment,
  UpdateProjectDocument,
  type UpdateProjectMutation,
  type UpdateProjectMutationVariables,
} from "@src/__generated__/graphql";
import type { TaskListingBulkActionsStore } from "@src/components/modules/resource-planning/tasks/listing/store/bulk-actions";
import type { IOption } from "@src/components/ui-kit";
import type { TaskModel } from "@src/components/widgets/Modals/ModalCommunication/models";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import { client } from "@src/services/client";
import { appStore } from "@src/stores/AppStore";
import type { TRefetch } from "@src/utils/apolloHelpers";
import { isFutureDate, toApiDate } from "@src/utils/dates";
import { required } from "@src/utils/forms/validators";
import mapToOptions from "@src/utils/map-to-options";
import { BooleanState } from "@src/utils/mobx/states/BooleanState";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { addMonths, isAfter } from "date-fns";
import { FieldState, FormState } from "formstate";
import { compact } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import { ProjectCreateBudgetItemTypeEnum } from "../create-budget-item-modal/store";
import { ProjectCreateTasksFromTemplateGroupStore } from "../create-tasks-from-task-template-group-modal/store";
import { OurWorkOverviewModel, ProjectModel } from "../models";
import { PlannedExpensesModalStore } from "../planned-expenses-modal/store";

export type ProjectCreateBudgetItemVariables =
  | {
      budgetItemType: ProjectCreateBudgetItemTypeEnum.Work;
      budgetItem: CreateOurWorkBudgetItemMutation["createOurWorkBudgetItem"];
      expenseBudgetCategoryId?: unknown;
    }
  | {
      budgetItemType: ProjectCreateBudgetItemTypeEnum.Expense;
      budgetItem: CreateExpenseBudgetItemMutation["createExpenseBudgetItem"];
      expenseBudgetCategoryId: string;
    };

export const PROJECT_NOTE_PREFIX = "<p><b>Note:</b> ";

type PipedriveDisclosureAdditionalData = {
  stage: ProjectStageFragment;
};

export class Store {
  billingPlanModal = new DisclosureState();
  @observable.ref fetchingFutureBill = false;
  @observable futureBill: number | undefined = undefined;
  @observable isReorderModeToggled = false;
  @observable updatingBudgetItems = false;
  @observable.ref selectedBudgetCategoryTabIndex = 0;
  @observable subRowsOpen = new BooleanState(true);
  @observable project?: ProjectModel;
  @observable.ref projectManagerOptions: IOption[] = [];

  refetchProject?: () => void;
  refetchGraph?: TRefetch<ProjectGraphQueryVariables, ProjectGraphQuery>;
  pipedriveForm = this.createPipedriveForm();

  private ourWorkOverviewFetcher = new FetchHelper<
    OurWorkOverviewForProjectQuery,
    OurWorkOverviewForProjectQueryVariables
  >(OurWorkOverviewForProjectDocument);
  private ourWorkOverviewDoneTasksFetcher = new FetchHelper<
    OurWorkOverviewDoneTasksForProjectQuery,
    OurWorkOverviewDoneTasksForProjectQueryVariables
  >(OurWorkOverviewDoneTasksForProjectDocument);
  @computed get isOurWorkOverviewLoading(): boolean {
    return this.ourWorkOverviewFetcher.isLoading.value;
  }

  private projectManagersFetcher = new FetchHelper<
    ProjectManagersSelectOptionsQuery,
    ProjectManagersSelectOptionsQueryVariables
  >(ProjectManagersSelectOptionsDocument);
  private pipedriveMutator = new MutationHelper<
    UpdateProjectMutation,
    UpdateProjectMutationVariables
  >(UpdateProjectDocument);

  createTaskFromTemplateGroupStore =
    new ProjectCreateTasksFromTemplateGroupStore(appStore.UIStore);
  plannedExpensesModalStore = new PlannedExpensesModalStore();
  pipedriveDisclosure = new DisclosureState<PipedriveDisclosureAdditionalData>({
    onOpen: () => {
      this.fetchPipedriveData();
      this.pipedriveForm = this.createPipedriveForm(this.project);
    },
    onClose: () => {
      this.pipedriveForm = this.createPipedriveForm();
    },
  });

  createPipedriveForm(project?: ProjectModel) {
    return new FormState({
      team_id: new FieldState(project?.team.id).validators(required),
      project_manager_id: new FieldState(project?.projectManager.id).validators(
        required,
      ),
      other_project_manager_ids: new FieldState(
        project?.otherProjectManagers.map((manager) => manager.id),
      ).validators(required),
      start_date: new FieldState<Date | undefined>(
        project?.start_date ?? new Date(),
      ).validators(required),
      end_date: new FieldState<Date | undefined>(
        project?.expected_end_date ??
          addMonths(project?.start_date ?? new Date(), 1),
      ).validators(required),
    });
  }

  constructor(
    readonly taskListingBulkActionsStore: TaskListingBulkActionsStore,
  ) {
    makeObservable(this);
  }

  @computed
  get budgetItemsFromSelectedTasks():
    | SimpleTaskFragment["ourWorkBudgetItem"][]
    | undefined {
    if (!this.project) return undefined;
    const allTasks: SimpleTaskFragment[] = [];

    for (const overview of this.project.ourWorkOverview) {
      for (const item of overview.budgetItems) {
        for (const task of item.tasks) {
          allTasks.push(task);

          if (!task.subtasks?.length) continue;
          for (const subtask of task.subtasks) {
            allTasks.push(subtask);
          }
        }
      }
    }

    const selectedTasks = allTasks.filter((task) =>
      this.taskListingBulkActionsStore.isSelected(task.id),
    );
    const selectedBudgetItems: SimpleTaskFragment["ourWorkBudgetItem"][] = [];

    for (const task of selectedTasks) {
      if (!task.ourWorkBudgetItem) continue;
      if (
        selectedBudgetItems.findIndex(
          (item) => item?.id === task.ourWorkBudgetItem?.id,
        ) !== -1
      )
        continue;
      selectedBudgetItems.push(task.ourWorkBudgetItem);
    }

    return selectedBudgetItems;
  }

  @computed get defaultBudgetItemIdForBulkActions(): string | undefined {
    if (
      !this.budgetItemsFromSelectedTasks?.length ||
      this.budgetItemsFromSelectedTasks.length > 1
    )
      return undefined;
    return this.budgetItemsFromSelectedTasks[0]?.id;
  }

  @computed get defaultBudgetItemOptionsForBulkActions():
    | IOption[]
    | undefined {
    if (!this.defaultBudgetItemIdForBulkActions) return undefined;
    return mapToOptions.budgetItems(compact(this.budgetItemsFromSelectedTasks));
  }

  @computed
  get defaultProjectIdForBulkActions(): string | undefined {
    if (!this.project) return undefined;
    if (!this.defaultBudgetItemIdForBulkActions) return undefined;
    return this.project.id;
  }

  @computed
  get defaultProjectOptionForBulkActions(): IOption[] | undefined {
    if (!this.project) return undefined;
    if (!this.defaultProjectIdForBulkActions) return undefined;
    return mapToOptions.projects([this.project]);
  }

  @computed get isPipedriveLoading(): boolean {
    return (
      this.projectManagersFetcher.isLoading.value ||
      this.pipedriveMutator.isLoading.value ||
      this.pipedriveForm.validating
    );
  }

  @action.bound async fetchPipedriveData() {
    const [data, error] = await this.projectManagersFetcher.fetch();

    if (error) return;
    this.projectManagerOptions = mapToOptions.projectManagers.toOptions(
      data.projectManagerSimpleMap,
    );
  }

  @action.bound async updateProjectForPipedrive() {
    if (!this.project?.id) {
      appStore.UIStore.toast({
        status: "error",
      });
      captureEvent({
        message: "FE: Missing project ID for pipedrive update.",
      });
      return;
    }

    if (!this.pipedriveDisclosure.additionalData?.stage) {
      appStore.UIStore.toast({
        status: "error",
      });
      captureEvent({
        message: "FE: Missing new project stage for project update.",
      });
      return;
    }

    const validation = await this.pipedriveForm.validate();

    if (validation.hasError) return;

    const [, error] = await this.pipedriveMutator.mutate({
      input: {
        id: this.project?.id,
        start_date: validation.value.start_date.$
          ? toApiDate(validation.value.start_date.$)
          : null,
        expected_end_date: validation.value.end_date.$
          ? toApiDate(validation.value.end_date.$)
          : null,
        project_manager_id: validation.value.project_manager_id.$,
        other_project_manager_ids: validation.value.other_project_manager_ids.$,
        team_id: validation.value.team_id.$,
        is_template: false,
      },
      propagate_billable: false,
    });

    if (error) {
      return;
    }

    const success = await this.updateProjectStage(
      this.pipedriveDisclosure.additionalData.stage,
    );
    if (!success) return;

    appStore.UIStore.toast({
      status: "success",
      title: t`Project stage successfully updated.`,
    });
    this.pipedriveDisclosure.onClose();
    this.refetchProject?.();
  }

  @computed get pipedriveOtherManagerOptions(): IOption[] {
    return this.projectManagerOptions.filter(
      (option) =>
        option.value !== this.pipedriveForm.$.project_manager_id.value,
    );
  }

  @observable showDoneTasks = new BooleanState(true);
  @observable showHiddenOwbi = new BooleanState(false);

  @action setProject(src: ProjectDetailQuery) {
    const previousOverview = this.project?.ourWorkOverview;
    this.project = src?.project
      ? // eslint-disable-next-line lingui/no-unlocalized-strings
        new ProjectModel({
          ...src.project,
          note: src.project.note?.replace("<p>", PROJECT_NOTE_PREFIX),
        })
      : undefined;
    if (this.project && previousOverview) {
      this.project.ourWorkOverview = previousOverview;
    }
    this.fetchOurWorkOverview();
  }

  @action.bound
  async fetchOurWorkOverview() {
    if (!this.project) {
      captureEvent({
        message: "FE: Missing project ID for fetching our work overview.",
      });
      return;
    }

    const [data, error] = await this.ourWorkOverviewFetcher.fetch({
      projectId: this.project.id,
    });

    if (error || !data.project?.ourWorkOverview) return;
    this.project.ourWorkOverview = data.project.ourWorkOverview.map(
      (overview) => new OurWorkOverviewModel(overview),
    );

    const ourWorkBudgetItemHasOnlyOneTask = data.project.ourWorkOverview
      .flatMap((s) => s.budgetItems)
      .every((budgetItem) => budgetItem.task_count === 1);

    if (ourWorkBudgetItemHasOnlyOneTask) {
      this.subRowsOpen.off();
    }

    this.refreshTaskListingBulkActionIds();

    if (this.showDoneTasks.value) {
      await this.fetchDoneTasks();
      this.setBudgetItemsShowDoneTasks(true);
    }
  }

  @action.bound
  refreshTaskListingBulkActionIds() {
    if (!this.project) return;

    const allIds: string[] = [];
    for (const ourWork of this.project.ourWorkOverview) {
      for (const budgetItem of ourWork.budgetItems) {
        for (const task of budgetItem.tasks) {
          allIds.push(task.id);
          if (!task.subtasks?.length) continue;
          for (const subtask of task.subtasks) {
            allIds.push(subtask.id);
          }
        }
      }
    }

    this.taskListingBulkActionsStore.setAllIds(allIds);
  }

  @action.bound
  setBudgetItemsShowDoneTasks(value: boolean) {
    if (!this.project) return;

    for (const overview of this.project.ourWorkOverview) {
      for (const budgetItem of overview.budgetItems) {
        budgetItem.setShowDoneTasks(value);
      }
    }
  }

  @action.bound
  async fetchDoneTasks(budgetItemId?: string) {
    if (!this.project) return;

    const targetBudgetItems = this.project.ourWorkOverview
      .flatMap((overview) => overview.budgetItems)
      .filter((budgetItem) => {
        if (budgetItem.doneTasksLoaded) return false;
        if (budgetItemId && budgetItem.id !== budgetItemId) return false;
        return true;
      });

    if (targetBudgetItems.length === 0) return;

    for (const budgetItem of targetBudgetItems) {
      budgetItem.setIsDoneTasksLoading(true);
    }

    const [data, error] = await this.ourWorkOverviewDoneTasksFetcher.fetch({
      projectId: this.project.id,
      budgetItemId,
    });

    for (const budgetItem of targetBudgetItems) {
      budgetItem.setIsDoneTasksLoading(false);
    }

    if (error || !data.project?.ourWorkOverview) return;

    const tasksByBudgetItemId = new Map(
      data.project.ourWorkOverview
        .flatMap((overview) => overview.budgetItems)
        .map((budgetItem) => [budgetItem.id, budgetItem.tasks] as const),
    );

    for (const budgetItem of targetBudgetItems) {
      const tasks = tasksByBudgetItemId.get(budgetItem.id);
      if (!tasks) continue;
      budgetItem.setTasks(tasks);
      budgetItem.setDoneTasksLoaded(true);
    }

    this.refreshTaskListingBulkActionIds();
  }

  @action.bound
  async toggleShowDoneTasks() {
    const nextValue = !this.showDoneTasks.value;

    if (nextValue) {
      await this.fetchDoneTasks();
      this.showDoneTasks.on();
      this.setBudgetItemsShowDoneTasks(true);
      return;
    }

    this.showDoneTasks.off();
    this.setBudgetItemsShowDoneTasks(false);
  }

  @action.bound setBudgetCategoryTabIndex(newIndex: number) {
    this.selectedBudgetCategoryTabIndex = newIndex;
  }

  @action.bound getBudgetItem(task: SimpleTaskForClientFragment | TaskModel) {
    return this.project?.ourWorkOverview
      .flatMap((i) => i.budgetItems)
      .find((i) => i.id === task.ourWorkBudgetItem?.id);
  }

  @action async fetchBillingPlan(
    id: ProjectBillingPlanDataQueryVariables["projectId"],
  ) {
    this.fetchingFutureBill = true;

    const { data } = await client
      .query<ProjectBillingPlanDataQuery, ProjectBillingPlanDataQueryVariables>(
        ProjectBillingPlanDataDocument,
        {
          projectId: id,
        },
      )
      .toPromise();

    this.fetchingFutureBill = false;

    if (!data) return;
    if (
      data.project?.expected_end_date &&
      new Date() === new Date(data.project?.expected_end_date)
    ) {
      this.futureBill = 0;
      return;
    }

    this.futureBill = data.project?.billingPlanByMonths.reduce(
      (prev, { invoiced_amount, items }) => {
        let itemsAmountSum = 0;
        const itemCount = items.length;
        for (let i = 0; i < itemCount; i++) {
          const { date, amount } = items[i];

          if (isAfter(new Date(), date)) continue;

          itemsAmountSum += amount;
        }

        if (itemsAmountSum === 0) return prev;
        if (itemsAmountSum <= invoiced_amount) return prev;
        return prev + (itemsAmountSum - invoiced_amount);
      },
      0,
    );
  }

  @action.bound restoreOurWorkBudgetItems(): void {
    this.project?.ourWorkOverview.forEach((overview) => {
      overview.restoreBudgetItems();
    });
  }

  @action setReorderMode(value: boolean): void {
    this.isReorderModeToggled = value;
  }

  @action.bound toggleReorderMode(): void {
    this.setReorderMode(!this.isReorderModeToggled);
  }

  @action.bound async updateOurWorkOverviewBudgetItems() {
    let error = false;
    if (!this.project?.ourWorkOverview) return;
    this.updatingBudgetItems = true;

    for (const overview of this.project.ourWorkOverview) {
      const err = await overview.updateBudgetItems();
      if (!err) continue;
      error = true;
    }

    this.updatingBudgetItems = false;

    if (error) return;
    this.setReorderMode(false);
  }

  @computed get projectStartsInFuture() {
    return isFutureDate(this.project?.start_date!);
  }

  @computed get isOurWorkTabSelected(): boolean {
    return this.selectedBudgetCategoryTabIndex === 0;
  }

  @computed get hasHiddenOurWorkItems(): boolean {
    if (!this.project?.ourWorkOverview) return false;
    return this.project?.ourWorkOverview.some((overview) =>
      overview.budgetItems.some((item) => item.hidden),
    );
  }

  @computed get hasOurWorkItems(): boolean {
    if (!this.project) return false;
    return (
      this.isOurWorkOverviewLoading ||
      this.project?.ourWorkOverview.some((item) => item.budgetItems.length > 0)
    );
  }

  @computed get doneTaskCount(): number {
    if (!this.project) return 0;
    let count = 0;

    for (const { budgetItems } of this.project.ourWorkOverview) {
      for (const { doneTaskCount } of budgetItems) {
        count += doneTaskCount;
      }
    }

    return count;
  }

  @computed get hiddenOwbiCount(): number {
    if (!this.project) return 0;
    let count = 0;

    for (const { budgetItems } of this.project.ourWorkOverview) {
      for (const { hidden } of budgetItems) {
        if (!hidden) continue;
        count++;
      }
    }

    return count;
  }

  @computed get selectedTabExpenseBudgetCategoryId() {
    if (!this.project) return undefined;
    if (this.isOurWorkTabSelected) return undefined;

    return this.project.expensesStructured?.[
      // NOTE: -1 because first is always Work tab
      this.selectedBudgetCategoryTabIndex - 1
    ]?.budget_category_id;
  }

  @action.bound onCreateBudgetItem({
    budgetItem,
    budgetItemType,
    expenseBudgetCategoryId,
  }: ProjectCreateBudgetItemVariables) {
    if (!this.project) return;
    if (!budgetItem) return;

    if (budgetItemType === ProjectCreateBudgetItemTypeEnum.Work) {
      this.project.ourWorkOverview
        .find((ourWork) => ourWork.name === "Our work")
        ?.addBudgetItem(budgetItem);
    } else {
      this.project.expensesStructured
        .find(
          (expense) => expense.budget_category_id === expenseBudgetCategoryId,
        )
        ?.addBudgetItem(budgetItem);
    }
  }

  @action.bound
  updateExpenseBudgetItemsHasPlannedExpenses(
    budgetItemIds: string[],
    hasPlannedExpenses: boolean,
  ) {
    if (!this.project) return;

    const budgetItemIdsSet = new Set(budgetItemIds);

    for (const structuredExpense of this.project.expensesStructured) {
      for (const budgetItem of structuredExpense.budgetItems) {
        if (!budgetItemIdsSet.has(budgetItem.id)) continue;
        budgetItem.updateHasPlannedExpenses(hasPlannedExpenses);
      }
    }
  }

  @action.bound
  updateOurWorkBudgetItemsHasPlannedExpenses(
    budgetItemIds: string[],
    hasPlannedExpenses: boolean,
  ) {
    if (!this.project) return;

    const budgetItemIdsSet = new Set(budgetItemIds);

    for (const overview of this.project.ourWorkOverview) {
      for (const budgetItem of overview.budgetItems) {
        if (!budgetItemIdsSet.has(budgetItem.id)) continue;
        budgetItem.updateHasPlannedExpenses(hasPlannedExpenses);
      }
    }
  }

  @action.bound handleOpenCreateTaskFromTemplateGroupModal(
    budgetItemId: string,
  ) {
    if (!this.refetchProject || !this.project) {
      appStore.UIStore.toast({
        status: "error",
      });
      captureEvent({
        message: "FE: Function refetchProject or project isn't defined.",
      });

      return;
    }

    this.createTaskFromTemplateGroupStore.disclosure.onOpen({
      onSubmit: this.refetchProject,
      ourWorkOverview: this.project.ourWorkOverview,
      initialSelectedId: budgetItemId,
    });
  }

  @action.bound async handleChangeProjectStage(stage: ProjectStageFragment) {
    if (stage?.system_name === "won") {
      this.pipedriveDisclosure.onOpen({ stage });
    } else {
      this.updateProjectStage(stage);
    }
  }

  @action.bound async updateProjectStage(stage: ProjectStageFragment) {
    if (!this.project?.id) {
      appStore.UIStore.toast({ status: "error" });
      captureEvent({
        message: "FE: Missing project ID for changing project stage.",
      });
      return false;
    }

    const data = await appStore.pipedriveStore.changeProjectStage(
      this.project.id,
      stage.id,
    );

    if (!data) return false;

    this.project.stage = data.project_stage;
    return true;
  }

  @computed
  get projectCategoriesCount(): number {
    return this.project?.categories.length ?? 0;
  }
}
